import { AnyAction, ThunkAction } from "@reduxjs/toolkit";
import axios, { baseUrl } from "../axios/axios";
import { RootState } from "./index";
import { consultPharmacyContextActions } from "./consultPharmacyContext-slice";
import { getPharmacistProfile } from "../services/pharmacistProfileService";
import { MarketplaceResponse } from "../types/shared";
import { PharmacistProfileResponse } from "../types/pharmacistProfile.types";

/**
 * Discriminated on purpose. A bare `PharmacistProfileResponse | null` collapses
 * the 404 onboarding state and a failed request into the same `null`, and the
 * two mean opposite things to a caller that is reconciling its editor against
 * server truth: "the row is gone, drop what you were editing" versus "I learned
 * nothing, keep it". The slice already refuses to conflate them (`fetchFailed`
 * PRESERVES the last known context); this is the same distinction, for callers.
 */
export type PharmacistProfileFetchResult =
  | { ok: true; profile: PharmacistProfileResponse | null }
  | { ok: false };

/**
 * ITAM-2622 (3.4) — ONE shared fetch for the adviser profile, consumed by both
 * the profile page and the context loader. A single source of truth is what
 * makes `404 -> create -> activation enabled` converge without a page reload.
 *
 * Never rejects: the failure is recorded in the slice and reported through the
 * result, so no call site needs a second error channel.
 */
export const fetchPharmacistProfile = (
  userId: number,
  signal?: AbortSignal,
): ThunkAction<
  Promise<PharmacistProfileFetchResult>,
  RootState,
  unknown,
  AnyAction
> => {
  return async (dispatch) => {
    dispatch(consultPharmacyContextActions.fetchStart());
    try {
      const profile = await getPharmacistProfile(userId, signal);
      dispatch(consultPharmacyContextActions.fetchSucceeded(profile));
      return { ok: true, profile };
    } catch (error) {
      console.error("ConsultPharmacyContext: profile fetch failed", error);
      dispatch(consultPharmacyContextActions.fetchFailed());
      return { ok: false };
    }
  };
};

/**
 * Enrichment ONLY — name/latitude/longitude for the item picker and the
 * delivery UI. Never an eligibility input: while it is absent the delivery UI
 * degrades exactly as today's null-detailed behavior does, and the item picker
 * works from the pair alone.
 *
 * Deliberately does NOT dispatch `setMarketplaceInfoDetailed`: that is the
 * RETAIL marketplace's detail, and decision 14 makes the two associations
 * independent.
 */
export const fetchConsultPharmacyDetail = (
  marketplaceId: number,
  branchMarketplaceId: number,
): ThunkAction<Promise<void>, RootState, unknown, AnyAction> => {
  return async (dispatch) => {
    dispatch(consultPharmacyContextActions.detailFetchStart());
    try {
      const response = await axios.get<MarketplaceResponse>(
        `${baseUrl}/marketplace/${marketplaceId}/${branchMarketplaceId}`,
      );
      dispatch(
        consultPharmacyContextActions.detailFetchSucceeded(response.data),
      );
    } catch (error) {
      console.error("ConsultPharmacyContext: detail fetch failed", error);
      dispatch(consultPharmacyContextActions.detailFetchFailed());
    }
  };
};
